// biome-ignore-all lint/a11y/noLabelWithoutControl: staff-only admin surface; the field captions are visual labels for the adjacent control, intentionally not wired via htmlFor (same convention as the other admin screens, Task #25).
//
// =============================================================================
// SectionAvailabilityAdmin — seasonal blocks on the wizard's request sections
// =============================================================================
// Admin-only screen (Manage → Section Availability). Near year-end Zephyr stops
// offering some sections (e.g. Devices, Uniforms) because late-year spend is
// often wasted when children change schools over Christmas.
//
// Table-backed (db-39, src/data/sectionBlocks.ts). The admin ADDS a block
// (section + date window + message); the list below shows every block with a
// DERIVED status (Scheduled / Live / Expired / Cancelled) — the list IS the
// permanent history, nothing is hidden. Click a row to edit within the rules
// (blockEditability): start locks once it's today/past, end can't be back-dated,
// message editable while active, expired/cancelled rows are read-only. To cancel
// a Scheduled or Live block, Deactivate it (soft — stays tagged Cancelled).
// Full design: docs/section-availability-table-plan.md.
// =============================================================================
import React from "react";
import {
  deactivateSectionBlock,
  insertSectionBlock,
  listSectionBlocks,
  updateSectionBlock,
} from "../../data/sectionBlocks";
import { invalidateSectionBlocksCache } from "../../hooks/useSectionBlocks";
import { alertDialog, confirmDialog } from "../../services/dialogService";
import { setNavGuard } from "../../services/navGuard";
import {
  blockEditability,
  blockStatus,
  SECTION_BLOCK_SECTIONS,
  sectionBlockLabel,
} from "../../services/sectionBlocks";
import { logAudit } from "../../utils/auditLog";
import { Btn } from "../../utils/ui.jsx";

const EMPTY_FORM = { section: "fees", start: "", end: "", message: "" };

// Visual style for each derived status badge.
const STATUS_STYLE = {
  scheduled: { tag: "Scheduled", color: "#1565C0", bg: "#E3F2FD" },
  live: { tag: "Live — blocking", color: "#C62828", bg: "#FFEBEE" },
  expired: { tag: "Expired", color: "#607D79", bg: "#ECEFF1" },
  cancelled: { tag: "Cancelled", color: "#8A4B00", bg: "#FFF3E0" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

export function SectionAvailabilityAdmin({ embedded = false } = {}) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [savedMsg, setSavedMsg] = React.useState("");
  const [error, setError] = React.useState("");
  const [form, setForm] = React.useState(EMPTY_FORM);
  // The row currently being edited (null = "Add a block" mode).
  const [editRow, setEditRow] = React.useState(null);
  // JSON of the form as it was loaded (for add/reset) — the dirty baseline.
  const cleanFormRef = React.useRef(JSON.stringify(EMPTY_FORM));
  const dirtyRef = React.useRef(false);

  const loadBlocks = React.useCallback(async function () {
    const data = await listSectionBlocks();
    setRows(Array.isArray(data) ? data : []);
    setLoading(false);
  }, []);

  React.useEffect(
    function () {
      loadBlocks();
    },
    [loadBlocks]
  );

  // Unsaved-changes guard: header navigation asks before leaving (navGuard),
  // and browser refresh/close gets the native prompt (beforeunload).
  React.useEffect(function () {
    const unregister = setNavGuard(function () {
      return dirtyRef.current ? "Your Section Availability changes haven't been saved." : null;
    });
    function onBeforeUnload(e) {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return function () {
      unregister();
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, []);

  function resetToAdd() {
    setForm(EMPTY_FORM);
    setEditRow(null);
    setError("");
    cleanFormRef.current = JSON.stringify(EMPTY_FORM);
  }

  function beginEdit(row) {
    const next = {
      section: row.section,
      start: row.start_date,
      end: row.end_date,
      message: row.message,
    };
    setForm(next);
    setEditRow(row);
    setError("");
    cleanFormRef.current = JSON.stringify(next);
  }

  function setField(field, val) {
    setForm(function (prev) {
      return { ...prev, [field]: val };
    });
  }

  // Editability of the row under edit (null in add mode → everything open).
  const edit = editRow
    ? blockEditability({
        start_date: editRow.start_date,
        end_date: editRow.end_date,
        deactivated: editRow.deactivated,
      })
    : null;
  const frozen = !!edit && !edit.startEditable && !edit.endEditable && !edit.messageEditable;

  function validate() {
    if (!form.start || !form.end || !form.message.trim()) {
      setError("Fill in the start date, end date AND message.");
      return false;
    }
    if (form.end < form.start) {
      setError("The end date is before the start date.");
      return false;
    }
    return true;
  }

  async function flashSaved() {
    invalidateSectionBlocksCache();
    setSavedMsg("Saved ✓");
    setTimeout(function () {
      setSavedMsg("");
    }, 3000);
    await loadBlocks();
  }

  async function saveAdd() {
    setError("");
    if (!validate()) return;
    setSaving(true);
    const res = await insertSectionBlock({
      section: form.section,
      start_date: form.start,
      end_date: form.end,
      message: form.message.trim(),
    });
    setSaving(false);
    if (res.error) {
      setError(`Save failed: ${res.error.message}`);
      return;
    }
    logAudit("section_block_created", {
      section: form.section,
      start: form.start,
      end: form.end,
    });
    resetToAdd();
    await flashSaved();
  }

  async function saveEdit() {
    setError("");
    if (frozen || !editRow) return;
    if (!validate()) return;
    // Only send the fields the rules allow to change.
    const patch = {};
    if (edit.startEditable && form.start !== editRow.start_date) patch.start_date = form.start;
    if (edit.endEditable && form.end !== editRow.end_date) patch.end_date = form.end;
    if (edit.messageEditable && form.message.trim() !== editRow.message)
      patch.message = form.message.trim();
    if (Object.keys(patch).length === 0) {
      resetToAdd();
      return;
    }
    setSaving(true);
    const res = await updateSectionBlock(editRow.id, patch);
    setSaving(false);
    if (res.error) {
      setError(`Save failed: ${res.error.message}`);
      return;
    }
    logAudit("section_block_updated", { id: editRow.id, section: editRow.section, ...patch });
    resetToAdd();
    await flashSaved();
  }

  async function deactivate() {
    if (!editRow || !edit?.canDeactivate) return;
    const ok = await confirmDialog({
      title: "Deactivate this block?",
      message: `Cancel the ${sectionBlockLabel(editRow.section)} block? It stops applying to new requests immediately and stays in the list tagged "Cancelled".`,
      confirmLabel: "Deactivate",
      danger: true,
    });
    if (!ok) return;
    setSaving(true);
    const res = await deactivateSectionBlock(editRow.id);
    setSaving(false);
    if (res.error) {
      await alertDialog({ message: `Could not deactivate: ${res.error.message}` });
      return;
    }
    logAudit("section_block_deactivated", { id: editRow.id, section: editRow.section });
    resetToAdd();
    await flashSaved();
  }

  // Recomputed every render; the stable guard/beforeunload handlers read the ref.
  dirtyRef.current = !saving && JSON.stringify(form) !== cleanFormRef.current;

  const endMin = edit ? edit.endMinDate : form.start || undefined;
  const inputCls =
    "w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white disabled:bg-[#F1F4F4] disabled:text-zephyr-muted";
  const labelCls = "text-[11px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1";

  return (
    <div className={embedded ? "" : "p-6 max-w-[1000px] mx-auto"}>
      {embedded ? (
        savedMsg && (
          <div className="mb-1 text-right">
            <span className="text-[13px] text-[#2E7D32] font-semibold">{savedMsg}</span>
          </div>
        )
      ) : (
        <div className="flex justify-between items-center mb-1">
          <h1 className="m-0 text-[22px] text-zephyr-text">Section Availability</h1>
          {savedMsg && <span className="text-[13px] text-[#2E7D32] font-semibold">{savedMsg}</span>}
        </div>
      )}
      <p className="m-0 mb-4 text-[13px] text-zephyr-muted">
        Temporarily stop shelters selecting a section on new requests (e.g. Devices near year end).
        While a block is live, ticking that section shows your message instead, and reopening an
        older draft removes the tick with the same message. Dates are inclusive and set explicitly
        each year. To cancel a block, open it and Deactivate — it stays below as history.
      </p>

      {/* Add / edit form */}
      <div className="border border-zephyr-border rounded-[10px] mb-6 py-4 px-4 bg-white">
        <div className="flex items-center gap-3 mb-3">
          <span className="font-bold text-[14px] text-zephyr-text">
            {editRow ? `Edit block — ${sectionBlockLabel(editRow.section)}` : "Add a block"}
          </span>
          {edit && (
            <span
              className="text-[10px] font-bold rounded-[4px] py-[2px] px-2"
              style={{
                color: STATUS_STYLE[edit.status].color,
                background: STATUS_STYLE[edit.status].bg,
              }}
            >
              {STATUS_STYLE[edit.status].tag}
            </span>
          )}
          {editRow && (
            <button
              type="button"
              onClick={resetToAdd}
              className="ml-auto bg-transparent rounded-[6px] py-1 px-[10px] text-[11px] cursor-pointer border border-[#CFE0DE] text-[#1A2E2B]"
              style={{ fontFamily: "inherit" }}
            >
              {frozen ? "Close" : "Cancel"}
            </button>
          )}
        </div>

        {frozen && (
          <div className="text-[12px] text-zephyr-muted mb-3">
            This block is {edit.status} and can no longer be edited — it's kept as a permanent
            record.
          </div>
        )}

        <div className="grid gap-3 mb-3" style={{ gridTemplateColumns: "220px 160px 160px" }}>
          <div>
            <label className={labelCls}>Section</label>
            <select
              value={form.section}
              disabled={!!editRow}
              onChange={function (e) {
                setField("section", e.target.value);
              }}
              className={inputCls}
              style={{ fontFamily: "inherit" }}
            >
              {SECTION_BLOCK_SECTIONS.map(function (s) {
                return (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                );
              })}
            </select>
          </div>
          <div>
            <label className={labelCls}>Blocked From</label>
            <input
              type="date"
              value={form.start}
              disabled={!!editRow && !edit.startEditable}
              onChange={function (e) {
                setField("start", e.target.value);
              }}
              className={inputCls}
              style={{ fontFamily: "inherit" }}
            />
          </div>
          <div>
            <label className={labelCls}>Blocked To</label>
            <input
              type="date"
              value={form.end}
              min={endMin}
              disabled={!!editRow && !edit.endEditable}
              onChange={function (e) {
                setField("end", e.target.value);
              }}
              className={inputCls}
              style={{ fontFamily: "inherit" }}
            />
          </div>
        </div>
        <div className="mb-3">
          <label className={labelCls}>Message shown to the DFV Service</label>
          <textarea
            value={form.message}
            disabled={!!editRow && !edit.messageEditable}
            onChange={function (e) {
              setField("message", e.target.value);
            }}
            rows={2}
            className={`${inputCls} box-border resize-y`}
            style={{ fontFamily: "inherit" }}
          />
        </div>

        {error && (
          <div className="text-[13px] text-[#C62828] font-semibold mb-3" role="alert">
            {error}
          </div>
        )}

        {!frozen && (
          <div className="flex items-center gap-3">
            <Btn onClick={editRow ? saveEdit : saveAdd} disabled={saving}>
              {saving ? "Saving..." : editRow ? "Save changes" : "Add block"}
            </Btn>
            {editRow && edit.canDeactivate && (
              <button
                type="button"
                onClick={deactivate}
                disabled={saving}
                className="bg-transparent rounded-[8px] py-2 px-4 text-[13px] cursor-pointer border border-[#C62828] text-[#C62828] font-semibold disabled:opacity-50"
                style={{ fontFamily: "inherit" }}
              >
                Deactivate
              </button>
            )}
          </div>
        )}
      </div>

      {/* All blocks (permanent history) */}
      <h2 className="mt-2 mb-1 text-[16px] text-zephyr-text">All blocks</h2>
      <p className="m-0 mb-3 text-[12px] text-zephyr-muted">
        Every block, newest first — including Expired and Cancelled ones (the permanent history).
        Click a row to view or edit it.
      </p>
      {loading ? (
        <div className="text-center text-zephyr-muted p-10">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="text-[13px] text-zephyr-muted italic">No blocks yet.</div>
      ) : (
        <div className="border border-zephyr-border rounded-[10px] overflow-hidden">
          <div
            className="grid gap-0 bg-zephyr-teal-dark text-white py-2 px-4 text-[11px] font-bold uppercase tracking-[0.5px]"
            style={{ gridTemplateColumns: "150px 190px 1fr 130px 120px" }}
          >
            <div>Section</div>
            <div>Blocked window</div>
            <div>Message</div>
            <div>Status</div>
            <div>Created</div>
          </div>
          {rows.map(function (r, i) {
            const status = blockStatus({
              start_date: r.start_date,
              end_date: r.end_date,
              deactivated: r.deactivated,
            });
            const style = STATUS_STYLE[status];
            const selected = editRow && editRow.id === r.id;
            return (
              <button
                type="button"
                key={r.id}
                onClick={function () {
                  beginEdit(r);
                }}
                className="grid gap-0 py-2 px-4 text-[12px] items-start text-left w-full cursor-pointer border-0"
                style={{
                  gridTemplateColumns: "150px 190px 1fr 130px 120px",
                  borderBottom: i < rows.length - 1 ? "1px solid #CFE0DE" : "none",
                  background: selected ? "#E0F2F1" : i % 2 === 0 ? "#FFFFFF" : "#F7F9F9",
                  fontFamily: "inherit",
                }}
              >
                <div className="font-semibold text-zephyr-text">{sectionBlockLabel(r.section)}</div>
                <div className="text-zephyr-text">{`${fmtDate(r.start_date)} – ${fmtDate(r.end_date)}`}</div>
                <div className="text-zephyr-muted whitespace-pre-wrap pr-3">{r.message}</div>
                <div>
                  <span
                    className="text-[10px] font-bold rounded-[4px] py-[2px] px-2"
                    style={{ color: style.color, background: style.bg }}
                  >
                    {style.tag}
                  </span>
                </div>
                <div className="text-zephyr-muted">{fmtDate(r.created_at)}</div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
